import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  Form,
  Textarea,
  RadioButton,
  FormItem,
  Tooltip,
} from '@frontend/design_system'
import { useAppSelector } from 'redux/hooks'
import {
  STOP_SELLS_DETAILS,
  STOP_SELLS_DETAILS_FORM_VALUES,
} from 'texts/stopSellsDetails'
import { BLOCK_TYPE_RADIO_BUTTONS, MAX_DATE, RADIO_BUTTONS } from './constants'
import { ContentWrapper } from 'ui/atoms/ContentWrapper'
import { SELECT_ALL_ITEM } from 'ui/atoms/Multiselect'
import { findSelectAllItem } from 'ui/atoms/Multiselect/utils'
import { FormValues, SelectValue, StopSellsDetailsFormProps } from './types'
import { supplierIdSelector } from 'redux/common/selectors'
import styles from './styles.module.scss'
import { StopSaleLocationForm } from './components/StopSaleLocationForm'
import { useApiRequest } from 'hooks/useApiRequest'
import { referencesApi } from 'api'
import { Country } from 'api/references/types'
import { allSymbolsAndEnglishLettersRegex } from 'constants/regex'
import { Rule } from 'ui/molecules/Form/components/FormItem/types'
import { FormChildren, FormItemChildren } from 'types/form'
import { STOP_SALE_FORM_ID } from '../../constants'
import { UnsavedChangesModal } from 'ui/components/UnsavedChangesModal'
import { InfoIcon } from 'ui/icons'
import { StopSalesBlockType } from 'api/fleet/types'
import { getUtcDate } from 'utils/dateFormat'
import { isOperatorSelector } from 'redux/login/selectors'

export const StopSaleDetailsForm = ({
  initialValues,
  isAddForm,
  handleSubmit,
  isReadonlyMode,
}: StopSellsDetailsFormProps) => {
  const params = useParams()
  const supplierCompanyId = useAppSelector(supplierIdSelector)
  const [countries, setCountries] = useState<Country[]>([])
  const [isInvalidRange, setInvalidRange] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const isOperator = useAppSelector(isOperatorSelector)

  const supplierId = supplierCompanyId || params.companyId

  const { apiRequest, loading } = useApiRequest((companyId) =>
    referencesApi.getCountries(companyId)
  )

  useEffect(() => {
    if (supplierId) {
      const getCountries = async () => {
        const countriesResponse = await apiRequest(String(supplierId))
        if (countriesResponse) {
          setCountries(countriesResponse.data.items)
        }
      }
      getCountries()
    }
  }, [supplierId])

  const onSubmit = (data: FormValues, validate: boolean) => {
    let isInvalidRange
    if (data.from && data.to) {
      isInvalidRange = new Date(data.from) > new Date(data.to)
      setInvalidRange(isInvalidRange)
    }
    if (validate && !isInvalidRange) {
      setIsSubmitting(true)
      const countriesValue = data.countries as SelectValue[]
      const citiesValue = data.cities as SelectValue[]
      const locationsValue = data.locations as SelectValue[]
      const classesValue = data.carClasses as SelectValue[]

      const isAllCountriesSelected = findSelectAllItem(countriesValue)
      const isAllCitiesSelected =
        isAllCountriesSelected ||
        countriesValue.length > 1 ||
        findSelectAllItem(citiesValue)
      const isAllLocationsSelected =
        isAllCountriesSelected ||
        countriesValue.length > 1 ||
        findSelectAllItem(locationsValue)
      const isAllClassesSelected =
        isAllCountriesSelected || findSelectAllItem(classesValue)

      const filteredCountries = countriesValue.filter(
        (item) => item.value !== SELECT_ALL_ITEM.value
      )

      const filteredCities = citiesValue.filter(
        (item) => item.value !== SELECT_ALL_ITEM.value
      )

      const filteredLocations = locationsValue.filter(
        (item) => item.value !== SELECT_ALL_ITEM.value
      )

      const filteredCarClasses = classesValue.filter(
        (item) => item.value !== SELECT_ALL_ITEM.value
      )

      handleSubmit({
        ...data,
        countries: isAllCountriesSelected
          ? null
          : filteredCountries.map((el) => Number(el.value)) || [],
        cities: isAllCitiesSelected
          ? null
          : filteredCities.map((el) => Number(el.value)) || [],
        locations: isAllLocationsSelected
          ? null
          : filteredLocations.map((el) => Number(el.value)) || [],
        carClasses: isAllClassesSelected
          ? null
          : filteredCarClasses.map((el) => el.value) || [],
        companyID: Number(supplierId),
      })
    }
  }

  const handleResetError = () => {
    setInvalidRange(false)
  }

  const commentValidationRule: Rule[] = useMemo(
    () => [
      {
        type: 'pattern',
        value: allSymbolsAndEnglishLettersRegex,
        message: STOP_SELLS_DETAILS_FORM_VALUES.COMMENTS.VALIDATION_MESSAGE,
      },
    ],
    []
  )

  return (
    <Form
      initValues={initialValues}
      onSubmit={onSubmit}
      formId={STOP_SALE_FORM_ID}
      reduxId={STOP_SALE_FORM_ID}
      className={styles['form-wrapper']}
    >
      {({ values, isDirty }: FormChildren) => (
        <>
          <ContentWrapper
            className={styles.form}
            title={STOP_SELLS_DETAILS.FORM_TITLE}
          >
            <div>
              <UnsavedChangesModal isDirty={isDirty && !isSubmitting} />
              <div className={styles['details-block']}>
                {isOperator && (
                  <FormItem
                    id={STOP_SELLS_DETAILS_FORM_VALUES.BLOCK_TYPE.ID}
                    className={styles['radio-buttons']}
                  >
                    {({ value, onChange, onChangeField }: FormItemChildren) =>
                      BLOCK_TYPE_RADIO_BUTTONS.map(({ label, id, tooltip }) => (
                        <div className={styles['radio-button']}>
                          <RadioButton
                            labelTypographyName="Button2"
                            key={id}
                            id={id}
                            label={label}
                            onChange={(id: string) => {
                              onChange(id)
                              if (id === StopSalesBlockType.Permanent) {
                                onChangeField(
                                  getUtcDate(MAX_DATE),
                                  STOP_SELLS_DETAILS_FORM_VALUES.END_DATE.ID
                                )
                              }
                            }}
                            checked={id === value}
                            disabled={isReadonlyMode}
                          />
                          <Tooltip
                            popupContent={tooltip}
                            size="primary"
                            placement="bottom"
                            alignSelf="center"
                          >
                            <InfoIcon color="blue700" size="small" />
                          </Tooltip>
                        </div>
                      ))
                    }
                  </FormItem>
                )}
                <StopSaleLocationForm
                  values={values}
                  isReadonlyMode={isReadonlyMode}
                  countries={countries}
                  isAddForm={isAddForm}
                  isCountriesLoaded={loading}
                  blockType={values.blockType as StopSalesBlockType}
                  companyId={String(supplierId)}
                  isInvalidRange={isInvalidRange}
                  resetRangeError={handleResetError}
                />
                <FormItem
                  id={STOP_SELLS_DETAILS_FORM_VALUES.APPLICABLE.ID}
                  className={styles['radio-buttons']}
                >
                  {({ value, onChange }: FormItemChildren) =>
                    RADIO_BUTTONS.map(({ label, id }) => (
                      <RadioButton
                        labelTypographyName="Button2"
                        key={id}
                        id={id}
                        label={label}
                        onChange={onChange}
                        checked={id === value}
                        disabled={isReadonlyMode}
                      />
                    ))
                  }
                </FormItem>
              </div>
              <FormItem
                id={STOP_SELLS_DETAILS_FORM_VALUES.COMMENTS.ID}
                className={styles['form-item']}
                rules={commentValidationRule}
              >
                {({ value, onChange, error }: FormItemChildren) => (
                  <Textarea
                    placeholder={
                      STOP_SELLS_DETAILS_FORM_VALUES.COMMENTS.PLACEHOLDER
                    }
                    label={STOP_SELLS_DETAILS_FORM_VALUES.COMMENTS.LABEL}
                    value={value}
                    maxLength={250}
                    error={error}
                    onChange={onChange}
                    disabled={isReadonlyMode}
                  />
                )}
              </FormItem>
            </div>
          </ContentWrapper>
        </>
      )}
    </Form>
  )
}
